import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { compose } from 'recompose';
import { observer } from 'mobx-react';

import useComponentMounted from '../../../hooks/useComponentMounted';
import { getQueryVariable } from '../../../utils/queryParams';
import {
  DaysForRefreshInterval,
  DEFAULT_MEAL_REFRESH_DAYS,
  MealPlanAssignmentStatus,
} from '../../utils/mealPlan';
import MealPlanAssignment, { RefreshInterval } from '../../Model/MealPlanAssignment';
import User from '../../../Model/User';
import Product from '../../../Model/Product';
import UserNutritionProfile from '../../Model/UserNutritionProfile';
import UserNutritionMacroGoals from '../../Model/UserNutritionMacroGoals';
import UserActivityProfile from '../../Model/UserActivityProfile';
import WeeklyCheckin from '../../Model/WeeklyCheckin';
import ExternalCoachContext from '../ExternalCoachContext';
import UserContext from '../../../context/UserContext';

import MealPlanAssignmentContext, { initialValues } from './MealPlanAssignmentContext';

// The planner reads the client's latest weight and the one before it.
const CHECKINS_SHOWN = 2;

const MealPlanAssignmentContextProvider = ({
  children,
}) => {
  // Get refresh user id, redirect to meal plan dashboard if not present
  const userId = getQueryVariable('user');
  // Supplied by the notification deep link; absent for a plain bookmark.
  const preselectedProductId = getQueryVariable('productId');

  const isComponentMountedRef = useComponentMounted();

  const [isReady, setIsReady] = useState(initialValues.isReady);
  const [isInitialized, setIsInitialized] = useState(initialValues.isInitialized);

  // User documents states
  const [weeklyCheckinDocs, setWeeklyCheckinDocs] = useState(initialValues.weeklyCheckinDocs);
  const [mealPlanAssignmentDocs, setMealPlanAssignmentDocs] = useState(initialValues.mealPlanAssignmentDocs);
  const [heldProducts, setHeldProducts] = useState(initialValues.heldProducts);
  const [selectedProductId, setSelectedProductId] = useState(preselectedProductId || '');
  const [userDoc, setUserDoc] = useState(initialValues.userDoc);
  const [nutritionProfileDoc, setNutritionProfileDoc] = useState(initialValues.nutritionProfileDoc);
  const [macroGoalsDoc, setMacroGoalsDoc] = useState(initialValues.macroGoalsDoc);
  const [activityProfileDoc, setActivityProfileDoc] = useState(initialValues.activityProfileDoc);

  // Assignment and refresh flow states
  const [nutritionalGoalsAction, setNutritionalGoalsAction] = useState(initialValues.nutritionalGoalsAction);
  const [mealPlanAction, setMealPlanAction] = useState(initialValues.mealPlanAction);
  const [nutritionalGoals, setNutritionalGoals] = useState(initialValues.nutritionalGoals);
  const [newMealPlanData, setNewMealPlanData] = useState(initialValues.newMealPlanData);
  const [selectedWeightGoal, setSelectedWeightGoal] = useState(initialValues.selectedWeightGoal);
  const [assignmentNote, setAssignmentNote] = useState(initialValues.assignmentNote);

  const {
    coachDoc: {
      mealPlanConfig: {
        refreshInterval: mealPlanRefreshInterval,
      } = {},
    } = {},
  } = useContext(ExternalCoachContext) || {};

  // The coach whose tools these are, read from the route as every other coach-scoped read
  // here does. ExternalCoachContext keeps its own `coachId` internal and never exposes it.
  const { userId: coachId } = useContext(UserContext);

  useEffect(() => {
    const init = async () => {
      // Scoped to the acting coach: the products this doc resolves filter the plans below,
      // and an unscoped doc resolves whichever coach happens to rank first. Read before the
      // rest because the check-in window below is sized from the coaches it names.
      const user = await User.getById(userId, { viewAsCoachId: coachId });
      const [
        weeklyCheckinCol,
        userNutritionProfile,
        userMacroGoals,
        userActivityProfile,
      ] = await Promise.all([
        WeeklyCheckin.getByUser(userId, CHECKINS_SHOWN * Math.max(user?.coachIds.length ?? 0, 1)),
        UserNutritionProfile.getById(userId),
        UserNutritionMacroGoals.getById(userId),
        UserActivityProfile.getById(userId),
      ]);
      // Every coach's panel repeats the same check-in, and only the client's own numbers are read.
      // The window allows for every coach the client has ever held, since their panels are still here.
      const byCheckIn = new Map(weeklyCheckinCol.docs.map((doc) => [doc.checkInId ?? doc.id, doc]));
      const latestWeeklyCheckinDocs = [...byCheckIn.values()].slice(0, CHECKINS_SHOWN);

      const activeProductIds = user?.activeProductIds || [];
      const [assignments, heldProductDocs] = await Promise.all([
        MealPlanAssignment.getUserMealPlanAssignments(userId, user, coachId),
        Promise.all(activeProductIds.map((productId) => Product.getById(productId))),
      ]);

      const resolveStatus = (assignment) => {
        if (mealPlanRefreshInterval === RefreshInterval.NEVER || !assignment.lastUpdated?.toDate) {
          return MealPlanAssignmentStatus.MANUALLY_REFRESHABLE;
        }
        return moment.utc()
          .isAfter(moment.utc(assignment.lastUpdated.toDate())
            .add(DaysForRefreshInterval[mealPlanRefreshInterval] || DEFAULT_MEAL_REFRESH_DAYS, 'days'))
          ? MealPlanAssignmentStatus.NEEDS_REFRESH
          : MealPlanAssignmentStatus.ACTIVE;
      };

      // Written through the array, not the callback argument, which `no-param-reassign` forbids.
      assignments.forEach((assignment, index) => {
        assignments[index].status = resolveStatus(assignment);
      });

      const products = activeProductIds.map((productId, index) => ({
        productId,
        productName: heldProductDocs[index]?.name || '',
      }));

      /*
        A deep link wins, then their newest plan's product — but only one they still hold. A stale link
        or a pre-backfill plan would otherwise scope the next write to a product they do not have.
      */
      const isHeldProduct = (productId) => !!productId && activeProductIds.includes(productId);
      const openingProductId = [preselectedProductId, assignments[0]?.productId].find(isHeldProduct)
        || activeProductIds[0]
        || '';

      if (isComponentMountedRef.current) {
        setMealPlanAssignmentDocs(assignments);
        setHeldProducts(products);
        setSelectedProductId(openingProductId);
        setUserDoc(user);
        setNutritionProfileDoc(userNutritionProfile);
        setMacroGoalsDoc(userMacroGoals);
        setActivityProfileDoc(userActivityProfile);
        setWeeklyCheckinDocs(latestWeeklyCheckinDocs.reverse());
        setIsReady(true);
      }
    };

    if (!isReady) {
      init();
    }
  }, [
    isReady,
    isComponentMountedRef,
    userId,
    coachId,
    preselectedProductId,
    mealPlanRefreshInterval,
  ]);

  // Null when the selected product has no plan yet, so the page offers to assign rather than
  // leaving another product's plan on screen.
  const mealPlanAssignmentDoc = useMemo(() => {
    if (!selectedProductId) {
      return mealPlanAssignmentDocs[0] || null;
    }
    /*
      Must stay identical to `pickPlanForProduct` in `functions-22/src/utils/meal-plan.ts`: an exact
      product match, else a pre-backfill unscoped plan, never another product's.
    */
    return mealPlanAssignmentDocs.find((doc) => doc.productId === selectedProductId)
      || mealPlanAssignmentDocs.find((doc) => !doc.productId)
      || null;
  }, [selectedProductId, mealPlanAssignmentDocs]);

  // Only the array needs refreshing; the selected document falls out of it.
  const fetchMealPlanAssignmentDoc = useCallback(async () => {
    if (!mealPlanAssignmentDoc) {
      const assignments = await MealPlanAssignment.getUserMealPlanAssignments(userId, userDoc, coachId);
      if (isComponentMountedRef.current) {
        setMealPlanAssignmentDocs(assignments);
      }
    }
  }, [mealPlanAssignmentDoc, userId, userDoc, coachId, isComponentMountedRef]);

  // Some states initial values use some of the fetched documents, this useEffect handles that
  useEffect(() => {
    if (isReady && !isInitialized) {
      setSelectedWeightGoal(nutritionProfileDoc?.weeklyWeightGoal);
      setAssignmentNote(mealPlanAssignmentDoc?.customMealPlanIntroMessage || '');
      setIsInitialized(true);
    }
  }, [
    isReady,
    isInitialized,
    nutritionProfileDoc,
    mealPlanAssignmentDoc,
  ]);

  const contextValue = useMemo(() => ({
    isReady: isReady && isInitialized,
    userId,
    mealPlanAssignmentDoc,
    mealPlanAssignmentDocs,
    heldProducts,
    selectedProductId,
    setSelectedProductId,
    userDoc,
    nutritionProfileDoc,
    macroGoalsDoc,
    weeklyCheckinDocs,
    activityProfileDoc,
    fetchMealPlanAssignmentDoc,
    nutritionalGoalsAction,
    setNutritionalGoalsAction,
    mealPlanAction,
    setMealPlanAction,
    nutritionalGoals,
    setNutritionalGoals,
    newMealPlanData,
    setNewMealPlanData,
    selectedWeightGoal,
    setSelectedWeightGoal,
    assignmentNote,
    setAssignmentNote,
  }), [
    isReady,
    isInitialized,
    userId,
    mealPlanAssignmentDoc,
    mealPlanAssignmentDocs,
    heldProducts,
    selectedProductId,
    userDoc,
    nutritionProfileDoc,
    macroGoalsDoc,
    weeklyCheckinDocs,
    activityProfileDoc,
    fetchMealPlanAssignmentDoc,
    nutritionalGoalsAction,
    setNutritionalGoalsAction,
    mealPlanAction,
    setMealPlanAction,
    nutritionalGoals,
    setNutritionalGoals,
    newMealPlanData,
    setNewMealPlanData,
    selectedWeightGoal,
    setSelectedWeightGoal,
    assignmentNote,
    setAssignmentNote,
  ]);

  return (
    <MealPlanAssignmentContext.Provider value={contextValue}>
      {children}
    </MealPlanAssignmentContext.Provider>
  );
};

MealPlanAssignmentContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(MealPlanAssignmentContextProvider);
